import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getFullBalance, convertToUsdc } from '../../services/ExchangeService';
import { useIntl } from 'react-intl';
import logger from '../../utils/logger';
import { fmtMoney, fmtPrice, fmtQty, fmtPctSigned } from '../../utils/format';
import './Dashboard.css';

/**
 * Portfolio-centric "My Holdings" widget.
 * Surfaces the user's real spot holdings (valued in USDC) as a clickable list.
 * Clicking a row drives the top chart via props.onSelectSymbol(`${asset}USDC`).
 *
 * props:
 * - onSelectSymbol(symbol)  : sets the chart symbol in the Dashboard
 * - activeSymbol            : currently selected chart symbol (for row highlight)
 * - tickerData              : live ticker map keyed by symbol (24h % change)
 */
function MyHoldings(props) {

    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    // USDC is the platform quote — value holdings in USDC.
    const FIAT = 'USDC';

    const [holdings, setHoldings] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [msg, setMsg] = useState(null); // { type, text } inline convert feedback

    const load = useCallback(() => {
        setIsLoading(true);
        getFullBalance(FIAT, false)
            .then((info) => {
                const rows = Object.entries(info)
                    // keep only per-asset objects (drop the top-level fiatEstimate string)
                    .filter(item => typeof info[item[0]] === 'object' && item[0] !== 'fiatEstimate')
                    .map(item => ({
                        asset: item[0],
                        available: parseFloat(item[1].available) || 0,
                        onOrder: parseFloat(item[1].onOrder) || 0,
                        fiatEstimate: item[1].fiatEstimate,
                        avg: item[1].avg
                    }))
                    // hide dust / zero balances
                    .filter(b => (b.available + b.onOrder) > 0 && parseFloat(b.fiatEstimate) >= 0.01)
                    // biggest position first
                    .sort((a, b) => (parseFloat(b.fiatEstimate) || 0) - (parseFloat(a.fiatEstimate) || 0));

                setHoldings(rows);
                setTotal(info.fiatEstimate);
                setIsLoading(false);
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setIsLoading(false);
            });
    }, []);

    useEffect(() => { load(); }, [load]);

    // One-click convert a holding to USDC (cancels resting orders + market-sells).
    function handleConvert(asset) {
        if (!window.confirm(t('positions.convertConfirm'))) return;
        setMsg(null);
        convertToUsdc(asset)
            .then(() => { setMsg({ type: 'success', text: t('positions.converted') }); load(); })
            .catch(err => setMsg({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    // Map an asset to its USDC trading pair. Stable/quote coins map to themselves.
    function pairFor(asset) {
        if (asset === 'USDC') return 'BTCUSDC';
        return `${asset}USDC`;
    }

    function changeFor(asset) {
        const tk = props.tickerData && props.tickerData[`${asset}USDC`];
        if (!tk || tk.percentChange === undefined || tk.percentChange === null) return null;
        return parseFloat(tk.percentChange);
    }

    function priceFor(asset) {
        const tk = props.tickerData && props.tickerData[`${asset}USDC`];
        if (tk && tk.close) return fmtPrice(tk.close);
        return '—';
    }

    const rows = useMemo(() => holdings.map(h => {
        const pair = pairFor(h.asset);
        const isActive = props.activeSymbol === pair;
        const change = changeFor(h.asset);
        const qty = h.available + h.onOrder;
        const onActivate = () => props.onSelectSymbol && props.onSelectSymbol(pair);
        return (
            <tr
                key={`hold-${h.asset}`}
                className={`gh-row-click${isActive ? ' table-active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={onActivate}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } }}
                aria-label={t('dashboard.holdings.clickHint')}
                title={t('dashboard.holdings.clickHint')}>
                <td data-label={t('dashboard.holdings.asset')} className="text-gray-900 fw-bold">
                    <img className="me-2" width={16} alt="" onError={(e) => { e.target.style.display = 'none'; }} src={`/img/icons/black/${h.asset.toLowerCase()}.svg`} />
                    {h.asset}
                </td>
                <td data-label={t('dashboard.holdings.qty')} className="num text-gray-900">{fmtQty(qty)}</td>
                <td data-label={t('dashboard.holdings.price')} className="num text-gray-900">{priceFor(h.asset)}</td>
                <td data-label={t('dashboard.holdings.change24h')} className={`num ${change === null ? 'text-muted' : (change >= 0 ? 'gh-pos' : 'gh-neg')}`}>
                    {change === null ? '—' : fmtPctSigned(change)}
                </td>
                <td data-label={t('dashboard.holdings.value')} className="num gh-neutral fw-bold">{fmtMoney(h.fiatEstimate)}</td>
                <td data-label={t('common.actions')} onClick={(e) => e.stopPropagation()}>
                    <Link to={`/orders/${pair}`} className="btn btn-sm btn-primary py-0 px-2">
                        {t('dashboard.holdings.trade')}
                    </Link>
                    {h.asset !== 'USDC' && (
                        <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2 ms-1" onClick={() => handleConvert(h.asset)}>
                            → USDC
                        </button>
                    )}
                </td>
            </tr>
        );
    }), [holdings, props.activeSymbol, props.tickerData]);

    return (
        <div className="col-12 mb-4">
            <div className="card border-0 shadow">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h2 className="fs-5 fw-bold mb-0">{t('dashboard.holdings.title')}</h2>
                    <span className="text-muted">
                        {t('dashboard.holdings.totalValue')}:&nbsp;
                        <span className="num fw-bold gh-neutral">
                            {(total && !String(total).includes('NaN')) ? total : `~USDC 0.00`}
                        </span>
                    </span>
                </div>
                {msg && (
                    <div className={`px-3 pt-2 small ${msg.type === 'error' ? 'gh-neg' : 'gh-pos'}`}>{msg.text}</div>
                )}
                <div className="table-responsive divScroll">
                    <table className="table align-items-center table-flush table-sm table-hover tableFixHead gh-table-stack">
                        <thead className="thead-light">
                            <tr>
                                <th className="border-bottom" scope="col">{t('dashboard.holdings.asset')}</th>
                                <th className="border-bottom num" scope="col">{t('dashboard.holdings.qty')}</th>
                                <th className="border-bottom num" scope="col">{t('dashboard.holdings.price')}</th>
                                <th className="border-bottom num" scope="col">{t('dashboard.holdings.change24h')}</th>
                                <th className="border-bottom num" scope="col">{t('dashboard.holdings.value')}</th>
                                <th className="border-bottom" scope="col">{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                isLoading
                                    ? (<tr><td colSpan={6} className="text-muted">{t('common.loading')}</td></tr>)
                                    : rows.length
                                        ? rows
                                        : (<tr><td colSpan={6} className="text-muted">{t('dashboard.holdings.empty')}</td></tr>)
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default MyHoldings;
